/**
 * Segmented control — the one switch pattern in the app.
 *
 * Replaces the design system's underline `Tabs` wherever we're switching a view
 * rather than navigating a document. Two reasons: the underline style reads as
 * a page-level tab bar and we already have one of those (`SubNav`), and, more
 * practically, it was the only control in the app still styled purely by
 * Tailwind classes, so it drifted from everything around it.
 *
 * `role="group"` with `aria-pressed`, not `tablist` — nothing here has an
 * associated tabpanel, so claiming the tabs pattern would promise keyboard
 * behaviour we don't implement.
 */
export function SegTabs<T extends string>({
  value, onChange, options, label, size = 'md',
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string; hint?: string }[];
  label: string;
  size?: 'sm' | 'md';
}) {
  return (
    <div className={`vd-seg${size === 'sm' ? ' vd-seg--sm' : ''}`} role="group" aria-label={label}>
      {options.map(o => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          aria-pressed={value === o.value}
          className={`vd-seg__btn${value === o.value ? ' is-active' : ''}`}
        >
          <span>{o.label}</span>
          {o.hint && <em>{o.hint}</em>}
        </button>
      ))}
    </div>
  );
}
